import { Observable } from 'rxjs'
import { Location } from 'history'
import { WorkspaceDTO } from 'iam/dtos/workspace'

/**
 * Identifier.
 */
export type Id = string | undefined

/**
 * Creates an ID.
 * @param value
 */
export function Id(value?: string | null): Id {
  return value || undefined
}

/**
 * No ID.
 */
Id.none = undefined as Id

/**
 * Returns `true` if `ID` has an actual value.
 */
Id.some = (id: Id): id is string => !!id

/**
 * Asserts that ID is specified.
 */
Id.assert = (id: Id): string => {
  if (!id) {
    throw new Error('ID is required, but was empty.')
  }
  return id
}

/**
 * Represents a type `T` that can be `T` or `null`.
 */
export type Nullable<T> = T | null

/**
 * Represents a type `T` that can be `T`, `null`, or `undefined`.
 */
export type Nillable<T> = Nullable<T> | undefined

/**
 * Type for making a type for the `Prime`-style funcs.
 */
export type PrimeProps<T, K extends keyof T> =
  | (Omit<Partial<T>, K> & Record<K, Id>)
  | undefined

/**
 * Token getter.
 */
export type GetToken = () => string | null

/**
 * Basic factory.
 */
export type Factory<T> = () => T

/**
 * API configuration.
 */
export interface APIConfig {
  readonly baseURL: string
  readonly token$: Observable<string>
  /**
   * Will be added to every request AS WELL as added as params to any
   * socket connection handshake.
   */
  readonly globalParams?: Record<string, unknown>
}

/**
 * Cursor type.
 */
export type EncodedCursor = string | null

/**
 * Paged data using cursors, but using the sexier AIP format.
 */
export interface AipCursorPaged<T> {
  data: Array<T>
  next_page_token: EncodedCursor
  total_size?: number
}

/**
 * Helpers for the AipCursorPaged type.
 */
export const AipCursorPaged = {
  /**
   * Creates a new `AipCursorPaged` by mapping the data using `map`.
   */
  map: <T, R>(
    paged: AipCursorPaged<T>,
    map: (data: AipCursorPaged<T>['data']) => R[]
  ): AipCursorPaged<R> => ({ ...paged, data: map(paged.data) }),
  /**
   * Creates a new `AipCursorPaged` from a `CursorPaged`.
   * @param paged
   */
  fromCursorPaged: <T>(paged: CursorPaged<T>): AipCursorPaged<T> => ({
    data: paged.data,
    next_page_token: paged.cursor,
    total_size: paged.total,
  }),
  /**
   * Creates a new `AipCursorPaged` from the given array.
   */
  fromArray: <T>(
    values: T[],
    cursor: Nullable<string> = null
  ): AipCursorPaged<T> => ({
    next_page_token: cursor,
    data: values,
  }),
  /**
   * Empty page.
   */
  empty: {
    data: [],
    next_page_token: null,
  } as AipCursorPaged<any>,
}

/**
 * Paged data using cursors.
 */
export interface CursorPaged<T> {
  data: Array<T>
  cursor: EncodedCursor
  total?: number
}

/**
 * Helpers for the CursorPaged type.
 */
export const CursorPaged = {
  /**
   * Creates a new `CursorPaged` by mapping the data using `map`.
   */
  map: <T, R>(
    paged: CursorPaged<T>,
    map: (data: CursorPaged<T>['data']) => R[]
  ): CursorPaged<R> => ({ ...paged, data: map(paged.data) }),
  /**
   * Creates a new `CursorPaged` from an `AipCursorPaged`.
   * @param paged
   */
  fromAipCursorPaged: <T>(paged: AipCursorPaged<T>): CursorPaged<T> => ({
    data: paged.data,
    cursor: paged.next_page_token,
    total: paged.total_size,
  }),
  /**
   * Creates a new `CursorPaged` from the given array.
   */
  fromArray: <T>(
    values: T[],
    cursor: Nullable<string> = null
  ): CursorPaged<T> => ({
    cursor,
    data: values,
  }),
  /**
   * Empty page.
   */
  empty: {
    data: [],
    cursor: null,
  } as CursorPaged<any>,
}

/**
 * Paged data using skip.
 */
export interface OffsetPaged<T> {
  data: Array<T>
  total?: number
  /**
   * The next value to pass to `skip`, if present.
   */
  skip?: number
}

/**
 * Helpers for the CursorPaged type.
 */
export const OffsetPaged = {
  /**
   * Creates a new `OffsetPaged` by mapping the data using `map`.
   */
  map: <T, R>(
    paged: OffsetPaged<T>,
    map: (data: T[]) => R[]
  ): OffsetPaged<R> => ({ ...paged, data: map(paged.data) }),
  /**
   * Empty page.
   */
  empty: {
    data: [],
  } as OffsetPaged<any>,
}

/**
 * Pagination query.
 */
export interface CursorPageQuery {
  limit?: number
  after?: EncodedCursor
}

/**
 * Pagination query using AIP convention.
 */
export interface AipCursorPageQuery {
  page_size?: number
  page_token?: EncodedCursor
}

/**
 * Paging using offset.
 */
export interface OffsetPageQuery {
  limit?: number
  skip?: number
}

/**
 * Page activity.
 */
export enum PageActivity {
  Active = 'Active',
  Inactive = 'Inactive',
  Exit = 'Exit',
}

/**
 * Used for showing desktop notifications.
 */
export interface SystemNotification {
  title: string
  body: string
  tag?: string
  icon?: string
  timeout?: number
  close$?: Observable<unknown>
  onClick?(): void
}

/**
 * Minimal interface for a router.
 */
export interface Router {
  /**
   * The location.
   */
  readonly location: Omit<Location, 'state'>

  /**
   * Push a route onto the stack.
   *
   * @param path
   */
  push(path: string): void

  /**
   * Replace a route on the stack.
   *
   * @param path
   */
  replace(path: string): void
}

declare global {
  interface Window {
    ga: any
    heap: any
    __env: any
    __workspaceDomain?: {
      workspace: WorkspaceDTO
    }
    __auth0Connections: string[]
    showSaveFilePicker(
      options?: ShowSaveFilePickerOptions
    ): Promise<FileSystemFileHandle>
  }

  interface ShowSaveFilePickerOptions {
    excludeAcceptAllOption?: boolean
    suggestedName?: string
    types?: Array<{ description?: string; accept: Record<string, string[]> }>
  }

  interface FileSystemFileHandle {
    createWritable(): WritableStream
  }
}
